import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import "./kanbanSuportePage.css";

const DND_MIME = "application/x-ticket-panel-kanban";

const t = (key, opts) =>
  i18n.t(`ticketPanelConfig.kanbanSuportePage.${key}`, opts);

const normalizeStages = (data) => {
  const st = data?.stages && typeof data.stages === "object" ? data.stages : {};
  const list = Array.isArray(st["1"]) ? st["1"] : [];
  return list.map((s) => ({
    id: String(s?.id ?? ""),
    name: String(s?.name ?? "").trim() || "—",
    color:
      typeof s?.color === "string" && /^#[0-9a-fA-F]{6}$/.test(s.color)
        ? s.color
        : "#9ca3af",
  }));
};

const buildCardsByStage = (stageList, ticketRows) => {
  const firstId = stageList[0]?.id;
  const map = {};
  stageList.forEach((s) => {
    map[s.id] = [];
  });
  const rows = Array.isArray(ticketRows) ? ticketRows : [];
  rows.forEach((raw) => {
    let sid = raw.supportKanbanStageId || firstId;
    if (!sid || !map[sid]) {
      sid = firstId;
    }
    if (!sid || !map[sid]) return;
    const y = new Date(raw.createdAt).getFullYear();
    const num = `${y}-${String(raw.id).padStart(4, "0")}`;
    map[sid].push({
      id: `ppt-${raw.id}`,
      ticketId: raw.id,
      num,
      title:
        (raw.subject && String(raw.subject).trim()) ||
        raw.contactName ||
        t("defaultCardTitle"),
      sub: raw.lastPreview || t("defaultCardSub"),
    });
  });
  return map;
};

const KanbanSuportePage = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [stages, setStages] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [cardsByStage, setCardsByStage] = useState({});
  const [dragOverStageId, setDragOverStageId] = useState(null);

  const refreshTickets = useCallback(async () => {
    const { data } = await api.get("/public-panel-tickets", {
      params: { situation: "in_progress" },
    });
    setTickets(Array.isArray(data) ? data : []);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data: cfg } = await api.get("/ticket-panel-config");
        if (cancelled) return;
        const list = normalizeStages(cfg);
        setStages(list);
        const { data: tix } = await api.get("/public-panel-tickets", {
          params: { situation: "in_progress" },
        });
        if (cancelled) return;
        setTickets(Array.isArray(tix) ? tix : []);
      } catch (err) {
        if (!cancelled) toastError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!stages.length) {
      setCardsByStage({});
      return;
    }
    setCardsByStage(buildCardsByStage(stages, tickets));
  }, [stages, tickets]);

  const getCards = useCallback(
    (stageId) => cardsByStage[stageId] || [],
    [cardsByStage]
  );

  const addCard = useCallback(
    async (stageId) => {
      try {
        const { data } = await api.post("/public-panel-tickets", {});
        if (data?.id) {
          await api.patch(`/public-panel-tickets/${data.id}`, {
            supportKanbanStageId: stageId,
          });
        }
        await refreshTickets();
      } catch (err) {
        toastError(err);
      }
    },
    [refreshTickets]
  );

  const moveCard = useCallback(
    async (fromStageId, cardId, toStageId) => {
      if (fromStageId === toStageId) return;
      const m = /^ppt-(\d+)$/.exec(cardId);
      if (!m) return;
      const ticketId = parseInt(m[1], 10);
      try {
        await api.patch(`/public-panel-tickets/${ticketId}`, {
          supportKanbanStageId: toStageId,
        });
        await refreshTickets();
      } catch (err) {
        toastError(err);
      }
    },
    [refreshTickets]
  );

  const openTicketInPublicPanel = useCallback((ticketId) => {
    if (ticketId == null || !Number.isFinite(Number(ticketId))) return;
    history.push(`/painel-ticket/ticket-publico?ticket=${Number(ticketId)}`);
  }, [history]);

  const onDragStart = (e, stageId, cardId) => {
    e.dataTransfer.setData(
      DND_MIME,
      JSON.stringify({ stageId, cardId })
    );
    e.dataTransfer.effectAllowed = "move";
  };

  const onDragEnd = () => {
    setDragOverStageId(null);
  };

  const onDragOver = (e, stageId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverStageId(stageId);
  };

  const onDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverStageId(null);
    }
  };

  const onDrop = (e, toStageId) => {
    e.preventDefault();
    setDragOverStageId(null);
    let payload;
    try {
      payload = JSON.parse(e.dataTransfer.getData(DND_MIME) || "{}");
    } catch {
      return;
    }
    if (!payload.stageId || !payload.cardId) return;
    moveCard(payload.stageId, payload.cardId, toStageId);
  };

  if (loading) {
    return (
      <div className="ticket-panel-kanban-root">
        <p style={{ color: "#6b7280" }}>{t("loading")}</p>
      </div>
    );
  }

  return (
    <div className="ticket-panel-kanban-root">
      <section id="ticket-publico" className="page active">
        <div className="kanban-topbar">
          <div>
            <h1>{t("title")}</h1>
            <p>{t("subtitle")}</p>
          </div>
        </div>

        {stages.length === 0 ? (
          <div
            className="kanban-board kanban-board--empty"
            id="kanban-board"
          >
            <div className="kanban-empty-msg">
              <div>{t("emptyStages1")}</div>
              <div style={{ marginTop: 10 }}>{t("emptyStages2")}</div>
            </div>
          </div>
        ) : (
          <div className="kanban-board" id="kanban-board">
            {stages.map((stage) => {
              const list = getCards(stage.id);
              return (
                <div key={stage.id} className="kanban-col">
                  <div
                    className="kanban-col-header"
                    style={{
                      borderTop: `4px solid ${stage.color}`,
                      borderRadius: "14px 14px 0 0",
                    }}
                  >
                    <div
                      className="kanban-col-dot"
                      style={{ background: stage.color }}
                    />
                    <span className="kanban-col-title">{stage.name}</span>
                    <span className="kanban-col-count">{list.length}</span>
                  </div>
                  <div
                    className={`kanban-col-body${
                      dragOverStageId === stage.id ? " drag-over" : ""
                    }`}
                    id={`kanban-col-body-${stage.id}`}
                    onDragOver={(e) => onDragOver(e, stage.id)}
                    onDragLeave={onDragLeave}
                    onDrop={(e) => onDrop(e, stage.id)}
                  >
                    {list.map((card) => (
                      <div
                        key={card.id}
                        className="kanban-card"
                        draggable
                        id={card.id}
                        onDragStart={(e) =>
                          onDragStart(e, stage.id, card.id)
                        }
                        onDragEnd={onDragEnd}
                        onDoubleClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          openTicketInPublicPanel(card.ticketId);
                        }}
                      >
                        <div className="card-id">#{card.num}</div>
                        <div className="card-title">{card.title}</div>
                        <div className="card-sub">{card.sub}</div>
                      </div>
                    ))}
                  </div>
                  <div className="kanban-col-footer">
                    <button
                      type="button"
                      className="btn-add-card"
                      onClick={() => addCard(stage.id)}
                    >
                      {t("addTicket")}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default KanbanSuportePage;
